<template>
  <lf-card class="mb-8 py-6">
    <template #header>
      <lf-h3>{{ $t("DEALS.TRACKING.UUID") }}</lf-h3>
    </template>

    <div
      class="my-8 grid grid-cols-1 gap-x-2 gap-y-4 px-6 2xl:grid-flow-col 2xl:grid-cols-3 xl:grid-cols-2"
    >
      <div class="flex">
        <div class="h-12 w-12">
          <icon-base :icon="IconLendflowLogoRound" width="48" height="48" />
        </div>
        <div class="ml-3 flex flex-col space-y-1">
          <span>
            {{ titleUUID }}
          </span>
          <text-display-with-clipboard
            :text="(isBusinessProfileView ? profile?.id : activeDeal?.id) || ''"
            scrollable="dealsWrapper"
            :max-width="330"
            class="border-gray-150 w-min rounded-sm border px-1-5 py-0-5 whitespace-nowrap text-headline"
          />
        </div>
      </div>
      <div v-if="!isBusinessProfileView || isClient" class="flex">
        <div class="h-12 w-12">
          <icon-base
            :icon="IconProfile"
            width="48"
            height="48"
            class="min-w-min text-disabled-medium"
          />
        </div>
        <div class="ml-3 flex flex-col space-y-1">
          <span>
            {{ $t("DEALS.TRACKING.CLIENT_UUID") }}
          </span>
          <div class="flex flex-col space-y-2">
            <div
              class="flex space-x-2 rounded-sm px-1-5 py-0-5 whitespace-nowrap"
              :class="{ 'border-gray-150 w-min border': hasPrimaryToken }"
            >
              <text-display-with-clipboard
                v-if="hasPrimaryToken"
                scrollable="dealsWrapper"
                class="text-headline"
                :text="primaryToken"
              />
              <div
                v-if="canAdd"
                data-cy="customer-uuid-edit"
                @click.prevent="openModal(primaryToken, PRIMARY_TOKEN)"
              >
                <icon-base
                  v-if="hasPrimaryToken"
                  :icon="IconEdit"
                  class="cursor-pointer text-disabled-medium"
                />
                <span
                  v-else
                  class="text-md border-gray-150 cursor-pointer rounded-sm border px-1-5 py-0-5 text-blue-600"
                >
                  + {{ $t("COMMON.ADD") }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!isBusinessProfileView" class="flex" data-cy="workflow-uuid">
        <div
          class="flex h-12 w-12 items-center justify-center rounded-full bg-disabled-medium"
        >
          <icon-base :icon="IconWorkflowView" class="text-white" />
        </div>
        <div class="ml-3 flex flex-col space-y-1">
          <span>
            {{ $t("DEALS.TRACKING.WORKFLOW_UUID") }}
          </span>
          <text-display-with-clipboard
            :text="activeDeal.workflow_template_id?.toString() || ''"
            scrollable="dealsWrapper"
            :max-width="300"
            class="border-gray-150 w-min rounded-sm border px-1-5 py-0-5 whitespace-nowrap text-headline"
          />
        </div>
      </div>
    </div>
    <template v-if="!isBusinessProfileView">
      <hr class="mx-6" />
      <div
        class="my-8 grid grid-cols-1 gap-x-2 gap-y-4 px-6 2xl:grid-flow-col 2xl:grid-cols-3 xl:grid-cols-2"
      >
        <div class="flex">
          <icon-base :icon="IconToken" width="48" height="48" class="min-w-min" />
          <div class="ml-3 flex flex-col space-y-1">
            <span>
              {{ $t("DEALS.TRACKING.ADDITIONAL_UUID", 2) }}
            </span>
            <hideable-elements
              :items="additionalTrackingTokens"
              :hide-breakpoint="HIDE_AFTER"
            >
              <template #element="{ index, item: token }">
                <div
                  :key="index"
                  class="border-gray-150 mr-2 mb-2 flex w-min rounded-sm border px-1-5 py-0-5 whitespace-nowrap"
                >
                  <text-display-with-clipboard
                    :text="(token as ApplicationAdditionalTrackingToken).token"
                    scrollable="dealsWrapper"
                    class="mr-3 text-headline"
                  />
                  <div
                    v-if="additionalTrackingTokens.length && canAdd"
                    @click.prevent="
                      openModal(
                        (token as ApplicationAdditionalTrackingToken).token,
                        SECONDARY_TOKEN,
                        index,
                        (token as ApplicationAdditionalTrackingToken).id
                      )
                    "
                  >
                    <icon-base
                      :icon="IconEdit"
                      class="cursor-pointer text-disabled-medium"
                    />
                  </div>
                </div>
              </template>

              <template #toggle="{ toggleShow, show }">
                <div class="mr-2 mb-2" @click.prevent="toggleShow">
                  <span
                    class="text-md border-gray-150 place-self-top flex grow cursor-pointer rounded-sm border px-1-5 py-0-5 align-top"
                  >
                    {{ show ? $t("COMMON.HIDE") : "..." }}
                  </span>
                </div>
              </template>

              <template #after>
                <div
                  v-if="
                    canAdd &&
                    additionalTrackingTokens.length < ADDITIONAL_IDS_LIMIT
                  "
                  data-cy="customer-tokens-edit"
                  @click.prevent="openModal('', SECONDARY_TOKEN)"
                >
                  <span
                    class="text-md border-gray-150 place-self-top flex grow cursor-pointer rounded-sm border px-1-5 py-0-5 align-top text-blue-600"
                  >
                    + {{ $t("COMMON.ADD") }}
                  </span>
                </div>
              </template>
            </hideable-elements>
          </div>
        </div>
        <div class="flex">
          <div
            class="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-disabled-medium"
          >
            <span class="font-bold text-white uppercase">
              {{ $t("DEALS.TRACKING.UTM") }}
            </span>
          </div>
          <div class="ml-3 flex flex-col space-y-1">
            <span>
              {{ $t("DEALS.TRACKING.UTM_PARAMETER", 2) }}
            </span>
            <hideable-elements
              :items="utmParameterSets"
              :hide-breakpoint="UTM_SETS_HIDE_AFTER"
            >
              <template #element="{ index, item: utmSet }">
                <div
                  :key="index"
                  class="border-gray-150 mr-2 mb-2 flex rounded-sm border px-1-5 py-0-5"
                >
                  <span class="mr-3 text-headline">
                    {{ formatUtmSet(utmSet as UtmParameterSet) }}
                  </span>
                  <div
                    v-if="canAdd"
                    @click.prevent="openUTMModal(utmSet as UtmParameterSet)"
                  >
                    <icon-base
                      :icon="IconEdit"
                      class="cursor-pointer text-disabled-medium"
                    />
                  </div>
                </div>
              </template>

              <template #toggle="{ toggleShow, show }">
                <div class="mr-2 mb-2" @click.prevent="toggleShow">
                  <span
                    class="text-md border-gray-150 place-self-top flex grow cursor-pointer rounded-sm border px-1-5 py-0-5 align-top"
                  >
                    {{ show ? $t("COMMON.HIDE") : "..." }}
                  </span>
                </div>
              </template>

              <template #after>
                <div
                  v-if="canAdd"
                  data-cy="add-utm-parameter"
                  @click.prevent="openUTMModal()"
                >
                  <span
                    class="text-md border-gray-150 place-self-top flex grow cursor-pointer rounded-sm border px-1-5 py-0-5 align-top text-blue-600"
                  >
                    + {{ $t("COMMON.ADD") }}
                  </span>
                </div>
              </template>
            </hideable-elements>
          </div>
        </div>
      </div>
    </template>
    <client-token-modal
      v-if="showModal"
      :is-business-profile-view="isBusinessProfileView"
      :profile-type="profileType"
      :token="token"
      :token-id="tokenId"
      :token-idx="tokenIndex"
      :is-primary-token="isPrimaryTokenModal"
      :close="closeModal"
      @additional-token-created="onAdditionalTokenCreated"
      @additional-token-updated="onAdditionalTokenUpdated"
      @additional-token-deleted="onAdditionalTokenDeleted"
    />
    <utm-parameter-modal
      v-if="showUTMModal"
      :set="selectedUtmSet"
      :close="closeUTMModal"
    />
  </lf-card>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { storeToRefs } from "pinia";

import { Entity } from "@/enums/entityProfiles";
import { formatUtmSet } from "@/helpers/tracking";
import { useAuth } from "@/hooks/auth";
import { useDeals } from "@/hooks/deals";
import clientsApiService from "@/services/modules/clients";
import useBusinessesStore from "@/stores/businesses";
import { useIndividualProfileStore } from "@/stores/individualProfile";

import IconEdit from "@/components/icons/IconEdit.vue";
import IconLendflowLogoRound from "@/components/icons/IconLendflowLogoRound.vue";
import IconProfile from "@/components/icons/sidebar/IconProfile.vue";
import IconToken from "@/components/icons/IconToken.vue";
import IconWorkflowView from "@/components/icons/workflow/IconWorkflowView.vue";
import HideableElements from "@/components/ui/HideableElements.vue";
import TextDisplayWithClipboard from "@/views/deals/components/TextDisplayWithClipboard.vue";
import ClientTokenModal from "@/views/deals/components/trackingView/ClientTokenModal.vue";
import UtmParameterModal from "@/views/deals/components/trackingView/UtmParameterModal.vue";

import type {
  ApplicationAdditionalTrackingToken,
  UtmParameterSet,
} from "@/models/applications";
import type { IBusinessEntityProfile } from "@/models/businesses";

const ADDITIONAL_IDS_LIMIT = 50;
const HIDE_AFTER = 3;
const UTM_SETS_HIDE_AFTER = 5;
const PRIMARY_TOKEN = "primaryToken";
const SECONDARY_TOKEN = "secondaryToken";

interface Props {
  isBusinessProfileView?: boolean;
  profileType?: Entity;
}

const { isBusinessProfileView = false, profileType = Entity.business } =
  defineProps<Props>();

const { isClient, isLendflowUser } = useAuth();
const { activeDeal } = useDeals();
const { t } = useI18n();
const individualProfileStore = useIndividualProfileStore();
const { currentIndividualProfile } = storeToRefs(individualProfileStore);
const businessesStore = useBusinessesStore();
const { activeBusinessEntityProfile } = storeToRefs(businessesStore);

const token = ref("");
const additionalTrackingTokens = ref<ApplicationAdditionalTrackingToken[]>([]);
const showModal = ref(false);
const showUTMModal = ref(false);
const isPrimaryTokenModal = ref(false);
const tokenId = ref<number>();
const tokenIndex = ref<number | undefined>();
const selectedUtmSet = ref<UtmParameterSet | undefined>();

const titleUUID = computed(() => {
  if (!isBusinessProfileView) {
    return t("DEALS.TRACKING.LENDFLOW_UUID");
  }

  return profileType === "business"
    ? t("DEALS.TRACKING.LENDFLOW_BUSINESS_UUID")
    : t("DEALS.TRACKING.LENDFLOW_INDIVIDUAl_UUID");
});

const profile = computed(() =>
  profileType === "business"
    ? activeBusinessEntityProfile.value
    : currentIndividualProfile.value
);

const canAdd = computed(() => isClient || isLendflowUser);

const primaryToken = computed(() =>
  isBusinessProfileView
    ? (profile.value as IBusinessEntityProfile | undefined)
        ?.client_tracking_token || ""
    : activeDeal.value.client_tracking_token || ""
);

const hasPrimaryToken = computed(() => !!primaryToken.value);

const openModal = (
  value = "",
  tokenType: string = SECONDARY_TOKEN,
  index?: number,
  id?: number
) => {
  tokenIndex.value = index;
  isPrimaryTokenModal.value = tokenType === PRIMARY_TOKEN;
  token.value = value;
  tokenId.value = id;
  showModal.value = true;
};

const closeModal = () => {
  tokenIndex.value = undefined;
  token.value = "";
  tokenId.value = undefined;
  showModal.value = false;
};

const utmParameterSets = computed(() => {
  if (!activeDeal.value) {
    return [];
  }
  const { utm_parameter_sets, utm_parameters } = activeDeal.value;

  if (utm_parameter_sets?.length) {
    return utm_parameter_sets;
  }

  /**
   * Fallback in case migration misses some deals
   */

  if (utm_parameters?.length) {
    const legacySet = utm_parameters.reduce<UtmParameterSet>(
      (acc, { type, value }) => {
        acc[type as keyof UtmParameterSet] = value;
        return acc;
      },
      { id: null }
    );
    return [legacySet];
  }

  return [];
});

const openUTMModal = (set?: UtmParameterSet) => {
  selectedUtmSet.value = set;
  showUTMModal.value = true;
};

const closeUTMModal = () => {
  selectedUtmSet.value = undefined;
  showUTMModal.value = false;
};

const onAdditionalTokenCreated = (newTokenId: number, tokenValue: string) => {
  if (isBusinessProfileView) {
    return;
  }

  additionalTrackingTokens.value.push({
    id: newTokenId,
    token: tokenValue,
  });
};

const onAdditionalTokenUpdated = (updatedTokenValue: string) => {
  if (isBusinessProfileView) {
    return;
  }

  additionalTrackingTokens.value = additionalTrackingTokens.value.map(
    (currentToken) => ({
      id: currentToken.id,
      token:
        tokenId.value === currentToken.id
          ? updatedTokenValue
          : currentToken.token,
    })
  );
};

const onAdditionalTokenDeleted = () => {
  if (isBusinessProfileView) {
    return;
  }

  additionalTrackingTokens.value = additionalTrackingTokens.value.filter(
    (currentToken) => currentToken.id !== tokenId.value
  );
};

onMounted(async () => {
  if (isBusinessProfileView || !activeDeal?.value.id) {
    return;
  }

  additionalTrackingTokens.value = (
    await clientsApiService.getAdditionalTrackingTokens({
      application_ids: [activeDeal.value.id],
    })
  ).data;
});
</script>
